/* QA review.
 *
 * Deliberately NOT the annotator's workspace with a different button. QA is
 * answering a different question — "is this batch good enough to send?" — and
 * needs to scan the whole video at once, with the annotator's rejections and
 * the judge's flags in the same list. The workspace is built for depth on one
 * sub-task at a time, which is the wrong shape for that.
 *
 * Approving pays the annotator and delivers. Rejecting sends it back with a
 * message that becomes part of the job's history, not a screen-only note —
 * which is why the message is mandatory: the whole point of the loop is that
 * the annotator is told what to fix.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { api } from "../api/client";
import type { JobDetail, Subtask } from "../api/types";
import { clock, counts, duration, money, subtaskTitle } from "../lib/format";
import { displayName } from "../lib/featured";
import { backToQueue } from "../lib/queuereturn";
import { Button, Spinner, StatusPill, VerdictBadge, useToast } from "../components/ui";
import { sourceAfterError } from "../lib/media";

export function QaReview() {
  const { id } = useParams();
  const jobId = Number(id);
  const navigate = useNavigate();
  // Only for "← Back": the queue's page rides in on the URL. lib/queuereturn.
  const location = useLocation();
  const toast = useToast();

  const [doc, setDoc] = useState<JobDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState<"all" | "attention">("attention");
  const [mediaFailed, setMediaFailed] = useState(false);
  // The file the QA video is showing when it is not doc.media_url: the master,
  // after the 360p copy failed to load.
  const [qaSrc, setQaSrc] = useState<string | null>(null);

  useEffect(() => {
    api.qaStart(jobId)
      .catch(() => api.job(jobId))   // already open, or not claimable — just read it
      .then(setDoc)
      .catch((e) => toast(e.message, "error"));
  }, [jobId, toast]);

  const rows = useMemo(() => {
    if (!doc) return [];
    const all = doc.tasks.flatMap((t) =>
      t.subtasks.map((s) => ({ task: t, sub: s })));
    if (filter === "all") return all;
    // What QA actually has to look at: anything the annotator rejected, and
    // anything the judge did not clear. Everything else is two independent
    // yes-votes and does not need a third.
    return all.filter(({ sub }) =>
      sub.status === "REJECTED" ||
      (sub.verdict && sub.verdict.decision !== "AUTO_ACCEPT"));
  }, [doc, filter]);

  const approve = useCallback(async () => {
    setBusy(true);
    try {
      await api.qaApprove(jobId, message.trim() || undefined);
      toast("Approved. The annotator has been paid and the job is delivered.");
      navigate(backToQueue(location.search));   // the queue page it came from, not page 1
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setBusy(false);
    }
  }, [jobId, message, navigate, toast, location.search]);

  const reject = useCallback(async () => {
    if (!message.trim()) {
      toast("Write what the annotator should fix — the message goes back with the job.", "error");
      return;
    }
    setBusy(true);
    try {
      await api.qaReject(jobId, message.trim());
      toast("Sent back to the annotator.");
      navigate(backToQueue(location.search));   // the queue page it came from, not page 1
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setBusy(false);
    }
  }, [jobId, message, navigate, toast, location.search]);

  if (!doc) return <div className="p-8"><Spinner label="Opening the job…" /></div>;

  const c = counts(doc.tasks);
  const minutes = (doc.video.duration_seconds ?? 0) / 60;
  const decided = doc.current_stage === "DELIVERED" ||
    doc.current_status === "APPROVED" || doc.current_status === "REJECTED";

  return (
    <div className="h-full overflow-y-auto">
      <header className="sticky top-0 z-10 border-b border-line
        bg-ground/95 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-4">
          {/* "Back", matching the workspace — the same control, and it must not
              be worded differently on the two screens that carry it. */}
          {/* Carries the queue's page back with it — point 5, 21 Sep.
              QA pages through the same queue the annotator does. */}
          <Link to={backToQueue(location.search)} title="Back to the video queue"
            className="rounded-control bg-ground px-3 py-1.5 text-sm
            text-text2 hover:bg-line">← Back</Link>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-extrabold tracking-[-0.01em] text-ink">
              QA · {displayName(doc.video.s3_path)}
            </h1>
            <p className="text-[11px] text-muted">
              {doc.tasks.length} tasks · {c.total} sub-tasks ·{" "}
              {duration(doc.video.duration_seconds)} ·{" "}
              annotator payout {money(minutes * doc.guideline.rate_per_minute, doc.guideline.currency)}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="text-verdict-approved-ink">{c.approved} approved</span>
            <span className="text-verdict-rejected-ink">{c.rejected} rejected</span>
            {/* Same split as the workspace — QA and the annotator must not be
                shown two different totals for the same video. lib/verdict.ts. */}
            {c.needsPerson > 0 && (
              <span className="text-verdict-flagged-ink">
                ▲ {c.needsPerson} need a human
              </span>
            )}
            {c.corrected > 0 && (
              <span className="text-muted">✎ {c.corrected} corrected by AI</span>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-5">
        {/* ------------------------------------------------- the video */}
        {/* A missing file leaves an empty native player that looks broken
            rather than absent, so say which — same reason as in Player.tsx. */}
        {doc.media_url && !mediaFailed ? (
          <video
            /* 26 Oct 2026: the 360p playback copy when there is one; if it
               fails to load, the master (media_fallback_url), then the notice. */
            src={qaSrc ?? doc.media_url}
            poster={doc.poster_url ?? undefined}
            preload="metadata"
            controls
            onError={() => {
              const next = sourceAfterError(qaSrc ?? doc.media_url, doc.media_fallback_url);
              if (next) setQaSrc(next); else setMediaFailed(true);
            }}
            className="mb-5 max-h-96 w-full rounded-well bg-navy9 border border-line"
          />
        ) : (
          <p className="mb-5 rounded-panel bg-card shadow-card px-4 py-6 text-center
            text-sm text-muted border border-line">
            The footage isn't on this machine — the review below is complete
            without it, but you cannot check a label against the video.
          </p>
        )}

        {/* --------------------------------------------------- filter */}
        <div className="mb-3 flex items-center gap-2">
          <Button
            onClick={() => setFilter("attention")}
            className={filter === "attention" ? "ring-teal" : ""}
          >
            Needs attention ({doc.tasks.flatMap((t) => t.subtasks).filter(
              (s) => s.status === "REJECTED" ||
                (s.verdict && s.verdict.decision !== "AUTO_ACCEPT")).length})
          </Button>
          <Button
            onClick={() => setFilter("all")}
            className={filter === "all" ? "ring-teal" : ""}
          >
            Everything ({c.total})
          </Button>
          <span className="ml-2 text-xs text-muted">
            "Needs attention" is what the annotator rejected plus what the AI
            judge would not clear.
          </span>
        </div>

        {/* ----------------------------------------------------- rows */}
        <ul className="flex flex-col gap-2">
          {rows.map(({ task, sub }) => (
            <Row key={sub.id} taskTitle={task.title} taskIndex={task.task_index}
              sub={sub} siblings={task.subtasks} />
          ))}
          {!rows.length && (
            <li className="rounded-panel bg-card shadow-card px-4 py-8 text-center
              text-sm text-muted border border-line">
              Nothing flagged. The annotator approved every sub-task and the AI
              judge cleared all of them.
            </li>
          )}
        </ul>

        {/* --------------------------------------------------- decision */}
        {!decided && (
          <section className="mt-6 rounded-panel bg-card shadow-card p-4 border border-line">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">
                Message to the annotator — required when sending it back,
                optional when approving.
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Sub-task 4 names the left hand but the right one is acting…"
                className="resize-none rounded-field bg-ground px-3 py-2 text-sm
                  border border-line placeholder:text-faint"
              />
            </label>
            <div className="mt-3 flex items-center gap-2">
              <Button variant="approve" disabled={busy} onClick={() => void approve()}>
                ✓ Approve, pay and deliver
              </Button>
              <Button variant="reject" disabled={busy} onClick={() => void reject()}>
                ⛔ Send back to the annotator
              </Button>
              <span className="ml-auto text-[11px] text-muted">
                Approving pays {money(minutes * doc.guideline.rate_per_minute, doc.guideline.currency)} for{" "}
                {minutes.toFixed(1)} minutes at {money(doc.guideline.rate_per_minute)}/min.
              </span>
            </div>
          </section>
        )}

        {/* ---------------------------------------------------- history */}
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-extrabold uppercase tracking-[0.08em] text-muted">
            HISTORY
          </h2>
          <ol className="flex flex-col gap-1 text-xs">
            {doc.events.map((e) => (
              <li key={e.id} className="flex gap-3 rounded-field bg-card px-3 py-1.5">
                <span className="w-40 shrink-0 font-mono text-muted">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span className="w-44 shrink-0 text-text2">
                  {e.stage} · {e.status}
                </span>
                <span className="w-24 shrink-0 text-muted">
                  {e.user_name ?? "AI"}
                </span>
                <span className="text-muted">{e.message}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function Row({ taskTitle, taskIndex, sub, siblings }: {
  taskTitle: string; taskIndex: number; sub: Subtask; siblings: Subtask[];
}) {
  return (
    <li className="rounded-panel bg-card shadow-card px-4 py-3 border border-line">
      <div className="flex items-start gap-3">
        <span className="w-28 shrink-0 font-mono text-[11px] text-muted">
          {clock(sub.start_seconds)}–{clock(sub.end_seconds)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-faint">
              T{taskIndex} · {taskTitle}
            </span>
            <span className="text-sm text-ink">
              {subtaskTitle(sub, siblings)}
            </span>
            <StatusPill status={sub.status} />
            {sub.verdict && (
              <VerdictBadge decision={sub.verdict.decision} score={sub.verdict.trust_score} />
            )}
            {sub.edited && <span className="text-[11px] text-teal">edited</span>}
          </div>
          <p className="mt-1 text-sm text-text2">{sub.description}</p>

          {sub.rejection_reason && (
            <p className="mt-1 text-xs text-verdict-rejected-ink">
              Annotator rejected — {sub.rejection_reason}
            </p>
          )}
          {sub.verdict?.reason_codes.length ? (
            <p className="mt-1 text-xs text-verdict-flagged-ink">
              AI judge: {sub.verdict.reason_codes.join(", ")}
            </p>
          ) : null}
          {sub.reason && (
            <p className="mt-1 text-xs text-muted">
              What the AI says it saw: {sub.reason}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
